const PARIS_TZ = 'Europe/Paris';

/**
 * API/DB store UTC wall-clock datetimes (often without "Z").
 * PHP (Europe/Paris) may serialize the same digits with a +02:00 offset.
 * We always interpret Y-M-D h:m:s components as UTC, then format in Paris.
 */
export function parseApiDateTime(value: string | Date | number): Date {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }

  const s = value.trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);
    const seconds = Number(match[6] ?? 0);
    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  }

  return new Date(s);
}

export function formatParisDateTime(
  value: string | Date | number,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
): string {
  const d = parseApiDateTime(value);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }
  return d.toLocaleString('fr-FR', { timeZone: PARIS_TZ, ...options });
}

/** Format a Date for datetime-local picker values (browser local time, no UTC shift). */
export function formatLocalDateTimeForPicker(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Picker value (local wall clock) → API/DB datetime string.
 * Keeps the same Y-M-D h:m digits as the picker (no timezone conversion),
 * matching how stream timestamps are stored and filtered server-side.
 */
export function localPickerValueToIso(
  value: string,
  options?: { inclusiveEnd?: boolean }
): string | undefined {
  if (!value) return undefined;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!match) return undefined;
  const seconds = options?.inclusiveEnd ? '59' : '00';
  return `${match[1]}T${match[2]}:${match[3]}:${seconds}`;
}
